import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useGuestStore } from '@/stores'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, User, Calendar, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Identicon } from '@/components/Identicon'
import { Skeleton } from '@/components/ui/skeleton'
import { GuestWeb, GuestLine, GuestUnblocked, GuestBlocked } from '@core/models'

const GuestDetail = observer(() => {
  const { tenantId, guestId } = useParams<{ tenantId: string; guestId: string }>()
  const navigate = useNavigate()
  const guestStore = useGuestStore()

  useEffect(() => {
    if (tenantId && guestId) {
      guestStore.getGuest(tenantId, guestId)
    }
  }, [tenantId, guestId, guestStore])

  const guest = guestStore.currentGuest

  const handleBack = () => {
    navigate(`/tenants/${tenantId}/guests`)
  }

  if (!guest) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-[200px]" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    )
  }

  const getTypeLabel = (type: number) => {
    switch (type) {
      case GuestWeb:
        return 'WEB'
      case GuestLine:
        return 'LINE'
      default:
        return '-'
    }
  }

  const getBlockedLabel = (blocked: number) => {
    switch (blocked) {
      case GuestUnblocked:
        return '友だち（ブロックされていません）'
      case GuestBlocked:
        return 'ブロック'
      default:
        return '-'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          戻る
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            ゲスト詳細
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {guest.avatar ? (
                <img 
                  src={guest.avatar} 
                  alt={guest.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <Identicon source={guest.id} size={80} />
              )}
            </div>
            
            <div className="flex-1 grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">ID</div>
                  <div className="font-mono text-sm">{guest.id}</div>
                </div>
                
                <div>
                  <div className="text-sm text-muted-foreground mb-1">名前</div>
                  <div className="font-medium">{guest.name}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">タイプ</div>
                  <Badge variant="outline">
                    {getTypeLabel(guest.type)}
                  </Badge>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">LINE UID</div>
                  <div>{guest.line_uid || '-'}</div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">ステータス</div>
                  <Badge 
                    variant={guest.blocked === GuestUnblocked ? 'default' : 'secondary'}
                  >
                    {guest.blocked !== undefined ? getBlockedLabel(guest.blocked) : '-'}
                  </Badge>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">ステータスメッセージ</div>
                  <div>{guest.status_message || '-'}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">言語</div>
                  <div>{guest.language || '-'}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    登録日時
                  </div>
                  <div>
                    {guest.created 
                      ? new Date(guest.created).toLocaleString('ja-JP')
                      : '-'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    更新日時
                  </div>
                  <div>
                    {guest.updated 
                      ? new Date(guest.updated).toLocaleString('ja-JP')
                      : '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
})

export default GuestDetail